import { SalesChannel } from './model';

// ─── Rule model ──────────────────────────────────────────────────────────────

/** Condition half of a flow rule. */
export interface FlowRuleCondition {
  /** Dropdown: matches a specific option key. */
  optionKey?: string;
  /** NumberField: comparison operator. */
  operator?: '>' | '<=' | '<' | '>=' | '=';
  /** NumberField: threshold value to compare against. */
  value?: number;
}

/** Action half of a flow rule. All fields are optional and combinable. */
export interface FlowRuleAction {
  /** Sales-channel string to route to (e.g. "FieldSales"). */
  route?: string;
  /** Skip the next N questions in the flow (relative). */
  skipNext?: number;
  /** Jump to the question at 1-based position N in the flow (absolute). */
  goTo?: number;
}

export interface FlowQuestionRule {
  when: FlowRuleCondition;
  then: FlowRuleAction;
}

// ─── Question registry ────────────────────────────────────────────────────────

/** Dropdown answer option — display data only, no routing. */
export interface QualifyingFlowOption {
  feId: string;
  /** Stable identifier referenced by FlowRuleCondition.optionKey. */
  key: string;
  text: { [lang: string]: string };
}

export interface QualifyingFlowQuestion {
  id: string;
  feId: string;
  questionKey: string;
  questionText: { [lang: string]: string };
  questionType: QuestionType;
  /** Populated for Dropdown questions. */
  options: QualifyingFlowOption[];
  /** True for NumberField questions that allow an empty (unanswered) response. */
  allowEmpty?: boolean;
  sortOrder: number;
  isActive: boolean;
}

export enum QuestionType {
  Dropdown = 'Dropdown',
  NumberField = 'NumberField',
}

// ─── Flow context ─────────────────────────────────────────────────────────────

/** A question slot inside a flow — question registry data + flow-specific rules. */
export interface FlowQuestion {
  feId: string;
  question: QualifyingFlowQuestion;
  rules: FlowQuestionRule[];
}

export interface QualificationFlow {
  id: number;
  feId: string;
  name: string;
  description: string;
  salesChannel: SalesChannel;
  isActive: boolean;
  questions: FlowQuestion[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const salesChannelLabel: Record<SalesChannel, string> = {
  [SalesChannel.MissingQueue]: 'Missing Queue',
  [SalesChannel.DigitalSales]: 'Digital Sales',
  [SalesChannel.SalesOS]: 'Sales OS',
  [SalesChannel.CrmSales]: 'CRM Sales',
  [SalesChannel.Revision]: 'Revision',
  [SalesChannel.SalesCall2]: 'Sales Call 2',
  [SalesChannel.FieldSales]: 'Field Sales',
  [SalesChannel.LeadQueue]: 'Lead Queue',
  [SalesChannel.SalesCall1Lead]: 'Sales Call 1 Lead',
  [SalesChannel.HeatSales]: 'Heat Sales',
  [SalesChannel.Retention]: 'Retention',
  [SalesChannel.SpecialSalesChannel1]: 'Special Sales Channel 1',
  [SalesChannel.SpecialSalesChannel2]: 'Special Sales Channel 2',
  [SalesChannel.SpecialSalesChannel3]: 'Special Sales Channel 3',
  [SalesChannel.SpecialSalesChannel4]: 'Special Sales Channel 4',
  [SalesChannel.SpecialSalesChannel5]: 'Special Sales Channel 5',
};
